import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '网络安全守护',
    url: 'https://wangluoanquan.cn',
    description: '致力于普及网络安全知识，帮助网民识别和防范流氓视频与网络诈骗',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'help@wangluoanquan.cn',
      contactType: 'customer support',
      availableLanguage: 'Chinese',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://wangluoanquan.cn', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { name: '关于我们' } },
    ],
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();

    if (honeypot) {
      setFormStatus({ type: 'success', message: '提交成功，我们会尽快回复！' });
      form.reset();
      return;
    }

    setSubmitting(true);

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9rhauvnahb7382ovmjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* ignore parse error */
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言！我们会尽快回复。' });
        form.reset();
      } else {
        const serverMsg = (parsed && typeof parsed === 'object'
          && (parsed as Record<string, unknown>).meta
          && typeof (parsed as Record<string, unknown>).meta === 'object'
          && ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message)
          ? String(((parsed as Record<string, unknown>).meta as Record<string, unknown>).message)
          : responseText || '提交失败，请稍后重试。';
        setFormStatus({ type: 'error', message: serverMsg });
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请检查网络连接后重试。' });
    } finally {
      setSubmitting(false);
    }
  };

  const values = [
    { icon: 'ri-heart-pulse-line', title: '守护安全', desc: '以保护每一位网民的数字安全为使命，做网络空间的守护者。' },
    { icon: 'ri-lightbulb-line', title: '科普先行', desc: '用通俗易懂的语言传播专业的网络安全知识，让安全意识深入人心。' },
    { icon: 'ri-group-line', title: '共建共享', desc: '汇聚社会各界力量，共建清朗网络空间，共享安全数字生活。' },
    { icon: 'ri-scales-line', title: '依法依规', desc: '严格遵守法律法规，引导用户通过合法渠道维护自身权益。' },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 网络安全守护 | 品牌介绍与联系方式"
        description="网络安全守护致力于普及网络安全知识，帮助网民识别和防范流氓视频与网络诈骗。了解我们的使命与价值观，获取联系方式。"
        keywords="关于我们,网络安全守护,品牌介绍,联系方式,网络安全科普"
        canonicalUrl="https://wangluoanquan.cn/about"
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/60">
            <div className="max-w-6xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center rounded-2xl bg-primary-100">
                <i className="ri-shield-flash-line text-3xl text-primary-600"></i>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
                关于网络安全守护
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto mb-4">
                <strong>网络安全守护</strong>是一个专注于网络安全科普的公益平台。我们致力于用通俗易懂的方式，
                向广大网民普及网络安全知识，帮助大家识别和防范各类网络威胁，特别是日益猖獗的<strong>流氓视频</strong>和<strong>视频诈骗</strong>行为。
              </p>
              <p className="text-base text-foreground-500 leading-relaxed max-w-2xl mx-auto">
                自2024年创立以来，我们已累计发布数十篇原创科普文章，覆盖了流氓视频定义、恶意内容识别、
                设备安全防护、法律维权指南等多个领域，累计服务超过百万用户。
              </p>
            </div>
          </section>

          {/* Values */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  我们的价值观
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                  四大核心理念，指引我们不断前行
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {values.map((v, i) => (
                  <div key={i} className="bg-background-50 rounded-lg border border-background-200/60 p-6 text-center">
                    <div className={`w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full ${
                      i === 0 ? 'bg-primary-100 text-primary-600' :
                      i === 1 ? 'bg-accent-100 text-accent-600' :
                      i === 2 ? 'bg-secondary-100 text-secondary-600' :
                      'bg-primary-100 text-primary-600'
                    }`}>
                      <i className={`${v.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  如有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Contact Info */}
                <div>
                  <div className="flex flex-col gap-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground-800 text-sm mb-1">电子邮箱</h4>
                        <p className="text-sm text-foreground-500">help@wangluoanquan.cn</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-time-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground-800 text-sm mb-1">响应时间</h4>
                        <p className="text-sm text-foreground-500">我们会在24小时内回复您的留言</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-information-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground-800 text-sm mb-1">内容纠错</h4>
                        <p className="text-sm text-foreground-500">如发现文章内容有误，欢迎指正</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="bg-background-100 rounded-lg border border-background-200/60 p-6">
                  <h3 className="font-heading font-semibold text-foreground-900 mb-5">发送留言</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    {/* Honeypot */}
                    <div className="form-hp-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <div className="flex flex-col gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm text-foreground-600 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm text-foreground-600 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm text-foreground-600 mb-1.5">
                          主题
                        </label>
                        <input
                          type="text"
                          id="contact-subject"
                          name="subject"
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                          placeholder="留言主题（选填）"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm text-foreground-600 mb-1.5">
                          留言内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors duration-200 resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                      </div>

                      {formStatus && (
                        <div
                          className={`text-sm px-4 py-3 rounded-lg ${
                            formStatus.type === 'success'
                              ? 'bg-primary-50 text-primary-700 border border-primary-200'
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}
                        >
                          {formStatus.message}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      >
                        {submitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-loader-4-line animate-spin"></i> 提交中...
                          </span>
                        ) : (
                          '发送留言'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}